import React from 'react';
import './KuerbisSchnitzen.css';
import KuerbisHeroImage from '../assets/img/event-kuerbis-schnitzen-hero.webp';
import { siteInfo } from '../data/siteInfo';
import { Seo, buildBreadcrumbSchema } from '../components/Seo';

const registrationMailHref = `mailto:${siteInfo.email}?subject=${encodeURIComponent('Anmeldung Kürbis schnitzen')}`;

const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: "Kürbis schnitzen im Sarita's",
    description: "Schnitz deinen Kürbis im Sarita's in Hinwil. Für Kinder in Begleitung eines Erwachsenen und für erwachsene Kinder. CHF 25.- inklusive Kürbis, Hauseistee und hausgemachten Brownies. Schnitzwerkzeug und Arbeitsfläche stehen bereit.",
    url: `${siteInfo.siteUrl}/kuerbis-schnitzen`,
    startDate: '2026-10-28T14:00:00+01:00',
    endDate: '2026-10-28T16:30:00+01:00',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    image: new URL(KuerbisHeroImage, siteInfo.siteUrl).toString(),
    location: {
        '@type': 'Place',
        name: siteInfo.name,
        address: {
            '@type': 'PostalAddress',
            streetAddress: siteInfo.streetAddress,
            postalCode: siteInfo.postalCode,
            addressLocality: siteInfo.addressLocality,
            addressCountry: siteInfo.addressCountry
        }
    },
    organizer: {
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.siteUrl,
        email: siteInfo.email,
        telephone: siteInfo.phoneIntl
    },
    offers: {
        '@type': 'Offer',
        url: `${siteInfo.siteUrl}/kuerbis-schnitzen`,
        price: '25',
        priceCurrency: 'CHF',
        availability: 'https://schema.org/InStock',
        validThrough: '2026-10-26T23:59:59+01:00'
    }
};

function KuerbisSchnitzen() {
    return (
        <div className='kuerbis-page'>
            <Seo
                title='Kürbis schnitzen'
                description="Kürbis schnitzen im Sarita's in Hinwil am Mittwoch, 28. Oktober 2026 von 14:00 bis 16:30 Uhr. CHF 25.- inklusive Kürbis und Verpflegung. Anmeldung bis 26. Oktober."
                path='/kuerbis-schnitzen'
                schema={[
                    eventSchema,
                    buildBreadcrumbSchema([
                        { name: 'Startseite', path: '/' },
                        { name: 'Aktuell', path: '/news' },
                        { name: 'Kürbis schnitzen', path: '/kuerbis-schnitzen' }
                    ])
                ]}
            />
            <header
                className='page-hero kuerbis-hero'
                style={{ backgroundImage: `url(${KuerbisHeroImage})` }}
            >
                <div className='container page-hero__content kuerbis-hero__content'>
                    <p className='kuerbis-hero__eyebrow'>Herbst im Sarita&apos;s</p>
                    <h1 className='page-hero__title'>Kürbis schnitzen</h1>
                    <p className='kuerbis-hero__meta'>Mittwoch, 28. Oktober 2026 &middot; 14:00 - 16:30 Uhr</p>
                </div>
            </header>

            <section className='section-shell'>
                <div className='container'>
                    <div className='kuerbis-intro panel-surface reveal'>
                        <div className='kuerbis-intro__copy'>
                            <p className='section-eyebrow'>Gemeinsam kreativ im Herbst</p>
                            <h2 className='section-title'>Schnitz deinen Kürbis im Sarita&apos;s</h2>
                            <p>
                                Ob lustiges Gesicht oder schauriges Grinsen: Am 28. Oktober wird es bei uns kreativ.
                                Wir halten dir einen Kürbis, Schnitzwerkzeug und eine Arbeitsfläche bereit.
                            </p>
                            <p>
                                Der Schnitznachmittag ist für Kinder in Begleitung eines Erwachsenen und für
                                erwachsene Kinder. Für das leibliche Wohl gibt es Sarita&apos;s Hauseistee und
                                hausgemachte Brownies.
                            </p>
                            <p>
                                Die Teilnahme kostet CHF 25.- inklusive Kürbis und Verpflegung.
                                Melde dich bis Montag, 26. Oktober 2026 per Mail oder Telefon an.
                            </p>
                            <div className='kuerbis-intro__actions'>
                                <a href={registrationMailHref} className='btn-sarita'>Per Mail anmelden</a>
                                <a href={siteInfo.phoneHref} className='btn-sarita btn-sarita--ghost'>Telefonisch anmelden</a>
                            </div>
                        </div>

                        <dl className='kuerbis-event-facts' aria-label='Eventdetails'>
                            <div>
                                <dt>Datum &amp; Zeit</dt>
                                <dd>Mittwoch, 28. Oktober 2026<br />14:00 - 16:30 Uhr</dd>
                            </div>
                            <div>
                                <dt>Ort</dt>
                                <dd>Sarita&apos;s Cafe &amp; Bistro<br />{siteInfo.addressLine1}<br />{siteInfo.addressLine2}</dd>
                            </div>
                            <div>
                                <dt>Teilnahme</dt>
                                <dd>CHF 25.-<br />inklusive Kürbis und Verpflegung</dd>
                            </div>
                            <div>
                                <dt>Für wen?</dt>
                                <dd>Kinder in Begleitung eines Erwachsenen und erwachsene Kinder</dd>
                            </div>
                            <div>
                                <dt>Anmeldung</dt>
                                <dd>
                                    Bis Montag, 26. Oktober 2026<br />
                                    Per Mail an <a href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a><br />
                                    oder telefonisch unter <a href={siteInfo.phoneHref}>{siteInfo.phone}</a>
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className='kuerbis-points'>
                        <article className='kuerbis-point reveal'>
                            <h3>Dein Kürbis</h3>
                            <p>Ein Kürbis ist im Preis inbegriffen und wartet darauf, von dir gestaltet zu werden.</p>
                        </article>
                        <article className='kuerbis-point reveal'>
                            <h3>Alles steht bereit</h3>
                            <p>Schnitzwerkzeug und eine Arbeitsfläche stehen dir bei uns zur Verfügung.</p>
                        </article>
                        <article className='kuerbis-point reveal'>
                            <h3>Eine feine Pause</h3>
                            <p>Sarita&apos;s Hauseistee und hausgemachte Brownies sind ebenfalls inklusive.</p>
                        </article>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default KuerbisSchnitzen;
